/**
 * Message Input Component
 * 
 * Input field for composing and sending messages with emoji support.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Input, Button } from 'antd';
import { SendOutlined, SmileOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { EmojiPicker } from './EmojiPicker';
import { MentionPicker } from './MentionPicker';
import { ReplyPreview } from './ReplyPreview';
import { CloseOutlined } from '@ant-design/icons';
import type { Conversation, Message } from '@municipality/types';
import { chatService } from '@municipality/chat';
import { useResponsive } from '@municipality/ui';

const { TextArea } = Input;

interface Participant {
  userId: string;
  name: string;
  avatar?: string;
}

interface MessageInputProps {
  onSendMessage: (content: string, mentions?: string[], replyTo?: string) => void;
  onTyping: (isTyping: boolean) => void;
  disabled?: boolean;
  conversation?: Conversation | null;
  currentUserId?: string;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
}

const InputContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile' && prop !== '$isTablet',
})<{ $isTablet: boolean; $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isMobile }) => ($isMobile ? '6px' : '8px')};
  padding: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};
  padding-bottom: ${({ $isMobile }) => ($isMobile ? 'calc(12px + env(safe-area-inset-bottom))' : '16px')};
  background-color: #fff;
  border-top: 1px solid #f0f0f0;
  position: relative;
  flex-shrink: 0;
  
  ${({ $isTablet, $isMobile }) =>
    ($isTablet || $isMobile) &&
    `
    position: sticky;
    bottom: 0;
    z-index: 10;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  `}
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
`;

const MentionPickerWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  left: 16px;
  right: 16px;
  margin-bottom: 8px;
  z-index: 1000;
`;

const StyledTextArea = styled(TextArea)`
  resize: none !important;
  
  &.ant-input {
    border-radius: 20px;
    padding: 10px 16px;
  }
`;

const SendButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  border-radius: 50% !important;
  width: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  height: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  min-width: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  min-height: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  max-width: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  max-height: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0;
  aspect-ratio: 1;
  overflow: hidden;
  
  .anticon {
    font-size: ${({ $isMobile }) => ($isMobile ? '18px' : '16px')};
  }
`;

const EmojiButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<{ $isMobile: boolean }>`
  border-radius: 50% !important;
  width: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  height: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  min-width: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  min-height: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  max-width: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  max-height: ${({ $isMobile }) => ($isMobile ? '44px' : '40px')} !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0;
  aspect-ratio: 1;
  overflow: hidden;
  
  .anticon {
    font-size: ${({ $isMobile }) => ($isMobile ? '18px' : '16px')};
  }
`;

const ReplyPreviewWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 0;
`;

const CancelReplyButton = styled(Button)`
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 0;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border: none;
  box-shadow: none;
  color: #999;
  z-index: 1;
  
  &:hover {
    color: #1890ff;
    background-color: transparent;
  }
`;

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTyping,
  disabled = false,
  conversation,
  currentUserId,
  replyingTo = null,
  onCancelReply,
}) => {
  const { t } = useTranslation('chat');
  const { isTablet, isMobile } = useResponsive();
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [mentionPickerOpen, setMentionPickerOpen] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState('');
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingUpdateRef = useRef<number>(0);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const TYPING_THROTTLE_MS = 2000;
  const cursorPositionRef = useRef<number | null>(null); // Store cursor position for emoji insertion (null = not set)
  const colonPositionRef = useRef<number | null>(null); // Store position of colon that triggered picker (null = no colon pending)
  const atPositionRef = useRef<number | null>(null); // Store position of @ that triggered mention picker
  const [enrichedParticipants, setEnrichedParticipants] = useState<Participant[]>([]);

  // Get participants for mention picker (group chats and direct chats with 2+ participants)
  // Fetch fresh user details at runtime so super_admin users with name+surname get correct full names
  const participants = useMemo<Participant[]>(() => {
    if (!conversation || !currentUserId || !conversation.participantIds?.length) {
      return [];
    }
    const isGroup = conversation.type === 'group';
    const isDirect = conversation.type === 'direct' && conversation.participantIds.length >= 2;
    if (!isGroup && !isDirect) {
      return [];
    }
    // Prefer enriched participants (from getUserDetailsBatch) when available
    if (enrichedParticipants.length > 0) {
      return enrichedParticipants;
    }
    // Fallback to participantDetails from Firestore (may have incomplete names for super_admin)
    const details = conversation.participantDetails ?? {};
    return conversation.participantIds
      .filter((userId) => userId !== currentUserId)
      .map((userId) => {
        const participantInfo = details[userId];
        return {
          userId,
          name: participantInfo?.name || 'Unknown',
          avatar: participantInfo?.avatar,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [conversation, currentUserId, enrichedParticipants]);

  // Enrich participant names from users collection (fixes super_admin name+surname)
  useEffect(() => {
    if (!conversation || !currentUserId || !conversation.participantIds?.length) {
      setEnrichedParticipants([]);
      return;
    }
    const isGroup = conversation.type === 'group';
    const isDirect = conversation.type === 'direct' && conversation.participantIds.length >= 2;
    if (!isGroup && !isDirect) {
      setEnrichedParticipants([]);
      return;
    }
    let cancelled = false;
    chatService.getUserDetailsBatch(conversation.participantIds).then((userDetails) => {
      if (cancelled) return;
      const list: Participant[] = conversation.participantIds
        .filter((userId) => userId !== currentUserId)
        .map((userId) => {
          const detail = userDetails[userId];
          const participantInfo = conversation.participantDetails[userId];
          const fullName = detail
            ? [detail.name, detail.surname].filter(Boolean).join(' ').trim() || detail.name || 'Unknown'
            : participantInfo?.name || 'Unknown';
          return {
            userId,
            name: fullName,
            avatar: participantInfo?.avatar,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setEnrichedParticipants(list);
    });
    return () => {
      cancelled = true;
    };
  }, [conversation, currentUserId]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);


  // Extract mentions from message content
  const extractMentions = useCallback((content: string): string[] => {
    if (!conversation || !participants.length) {
      return [];
    }

    // Match @username patterns in the message
    // Updated regex to match @name with spaces (e.g., "@John Doe")
    // The (?=\s|$) lookahead ensures the match stops at a space or end of string
    // Pattern matches @ followed by 1-2 words (most names are first name + last name)
    // For longer names, we'll try matching progressively more words in the participant matching logic
    // The pattern uses a non-greedy match to ensure it stops at the first space after the mention
    const mentionPattern = /@([^\s@]+(?:\s+[^\s@]+)?)(?=\s|$)/g;
    
    // Use matchAll or exec in a loop to properly capture all matches
    const matches: RegExpMatchArray[] = [];
    let match: RegExpExecArray | null;
    
    // Reset regex lastIndex
    mentionPattern.lastIndex = 0;
    
    while ((match = mentionPattern.exec(content)) !== null) {
      matches.push(match);
    }
    
    if (matches.length === 0) {
      return [];
    }

    // Extract user IDs for mentioned names
    const mentionedUserIds: string[] = [];
    matches.forEach((match) => {
      const name = match[1].trim(); // Captured group (name without @), trimmed
      
      // Find participant with matching name (exact match or starts with)
      const participant = participants.find((p) => {
        const participantNameLower = p.name.toLowerCase().trim();
        const mentionNameLower = name.toLowerCase().trim();
        
        // Check for exact match (case-insensitive)
        if (participantNameLower === mentionNameLower) {
          return true;
        }
        
        // Check if participant name starts with mention name followed by space
        // (e.g., "John Doe" starts with "John ")
        if (participantNameLower.startsWith(mentionNameLower + ' ')) {
          return true;
        }
        
        // Check if the first word(s) of the participant name match the mention
        // (e.g., "John Michael Doe" matches "John Michael")
        const participantWords = participantNameLower.split(' ');
        const mentionWords = mentionNameLower.split(' ');
        if (mentionWords.length <= participantWords.length) {
          const participantStart = participantWords.slice(0, mentionWords.length).join(' ');
          if (participantStart === mentionNameLower) {
            return true;
          }
        }
        
        return false;
      });
      
      if (participant) {
        mentionedUserIds.push(participant.userId);
      }
    });

    // Remove duplicates
    const uniqueMentions = [...new Set(mentionedUserIds)];
    
    return uniqueMentions;
  }, [conversation, participants]);

  const handleSend = () => {
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage) return;

    // Extract mentions before sending
    const mentions = extractMentions(trimmedMessage);

    // Send message with replyTo if replying
    onSendMessage(
      trimmedMessage,
      mentions.length > 0 ? mentions : undefined,
      replyingTo?.id
    );
    
    setMessage('');
    setIsTyping(false);
    onTyping(false);
    setMentionPickerOpen(false);
    setMentionSearchQuery('');
    atPositionRef.current = null;
    
    // Clear reply if replying
    if (replyingTo && onCancelReply) {
      onCancelReply();
    }

    // Clear timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  // Handle emoji selection
  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      if (!textAreaRef.current) return;

      let insertPosition: number;
      let textBefore: string;
      let textAfter: string;

      // Check if we're replacing a colon that was just typed
      if (colonPositionRef.current !== null) {
        // Replace colon with emoji
        const colonPos = colonPositionRef.current;
        textBefore = message.substring(0, colonPos);
        textAfter = message.substring(colonPos + 1); // Skip the colon
        insertPosition = colonPos;
        
        // Reset colon position
        colonPositionRef.current = null;
      } else {
        // Normal emoji insertion at cursor position (from button click)
        // Prioritize stored position (from when button was clicked) over current selection
        // This ensures we use the position from when picker was opened, not current position
        if (cursorPositionRef.current !== null) {
          // Use stored position (from button click)
          insertPosition = cursorPositionRef.current;
          textBefore = message.substring(0, insertPosition);
          textAfter = message.substring(insertPosition);
          
          // Reset stored position
          cursorPositionRef.current = null;
        } else {
          // Fallback: try to get current selection, but this might be 0 if textarea lost focus
          const textarea = textAreaRef.current;
          const currentStart = textarea?.selectionStart ?? 0;
          const currentEnd = textarea?.selectionEnd ?? 0;
          
          insertPosition = currentStart;
          textBefore = message.substring(0, currentStart);
          textAfter = message.substring(currentEnd);
        }
      }

      // Insert emoji at position
      const newMessage = textBefore + emoji + textAfter;
      
      setMessage(newMessage);

      // Set cursor position after inserted emoji
      const newPosition = insertPosition + emoji.length;
      
      // Use setTimeout to ensure textarea is still mounted and accessible
      setTimeout(() => {
        const textarea = textAreaRef.current;
        // Check if textarea exists and is a valid HTMLTextAreaElement
        if (textarea && textarea instanceof HTMLTextAreaElement && typeof textarea.setSelectionRange === 'function') {
          try {
            textarea.setSelectionRange(newPosition, newPosition);
            textarea.focus();
          } catch (error) {
            // Silently handle errors (textarea might be unmounted or readonly)
            console.warn('Failed to set cursor position:', error);
          }
        } else {
          console.warn('[Emoji Select] Textarea not available or invalid:', {
            exists: !!textarea,
            isTextArea: textarea instanceof HTMLTextAreaElement,
            hasSetSelectionRange: textarea && typeof textarea.setSelectionRange === 'function',
          });
        }
      }, 0);

      // Close emoji picker after selection
      setEmojiPickerOpen(false);

      // Trigger typing indicator
      if (!isTyping) {
        setIsTyping(true);
        onTyping(true);
      }
    },
    [message, isTyping, onTyping]
  );

  // Handle mention selection
  const handleMentionSelect = useCallback(
    (participant: Participant) => {
      if (!textAreaRef.current || atPositionRef.current === null) return;

      const textBefore = message.substring(0, atPositionRef.current);
      const textAfter = message.substring(
        atPositionRef.current + 1 + mentionSearchQuery.length
      );
      const newMessage = textBefore + `@${participant.name} ` + textAfter;
      
      setMessage(newMessage);
      setMentionPickerOpen(false);
      setMentionSearchQuery('');
      atPositionRef.current = null;

      // Set cursor position after inserted mention
      const newPosition = (atPositionRef.current ?? 0) + participant.name.length + 2; // +2 for @ and space
      setTimeout(() => {
        const textarea = textAreaRef.current;
        if (textarea && textarea instanceof HTMLTextAreaElement) {
          try {
            textarea.setSelectionRange(newPosition, newPosition);
            textarea.focus();
          } catch (error) {
            console.warn('Failed to set cursor position after mention:', error);
          }
        }
      }, 0);

      // Trigger typing indicator
      if (!isTyping) {
        setIsTyping(true);
        onTyping(true);
      }
    },
    [message, mentionSearchQuery, isTyping, onTyping]
  );

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Enter key to send message (but not if mention picker is open)
    if (e.key === 'Enter' && !e.shiftKey && !mentionPickerOpen) {
      e.preventDefault();
      handleSend();
      return;
    }

    // Handle Escape key to close pickers
    if (e.key === 'Escape') {
      if (mentionPickerOpen) {
        e.preventDefault();
        setMentionPickerOpen(false);
        setMentionSearchQuery('');
        atPositionRef.current = null;
        return;
      }
      if (emojiPickerOpen) {
        e.preventDefault();
        setEmojiPickerOpen(false);
        // Don't reset colonPositionRef - let user keep typing
        return;
      }
    }

    // Handle Backspace - if picker is open and we're at trigger position, close picker
    if (e.key === 'Backspace') {
      const textarea = e.currentTarget;
      const cursorPos = textarea.selectionStart || 0;
      
      if (mentionPickerOpen && atPositionRef.current !== null) {
        // If backspacing at or right after @ position, close mention picker
        if (cursorPos <= (atPositionRef.current + 1)) {
          atPositionRef.current = null;
          setMentionPickerOpen(false);
          setMentionSearchQuery('');
        }
      }
      
      if (emojiPickerOpen && colonPositionRef.current !== null) {
        // If backspacing at or right after colon position, close emoji picker
        if (cursorPos <= (colonPositionRef.current + 1)) {
          colonPositionRef.current = null;
          setEmojiPickerOpen(false);
        }
      }
    }

    // Handle @ key - open mention picker (reliable detection for all keyboard layouts)
    const isAtKey = e.key === '@' || (e.shiftKey && e.key === '2') || e.keyCode === 64;
    if (isAtKey && !e.ctrlKey && !e.metaKey && !e.altKey && conversation && participants.length > 0) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart ?? 0;
      const end = textarea.selectionEnd ?? 0;
      const newValue = message.substring(0, start) + '@' + message.substring(end);
      setMessage(newValue);
      atPositionRef.current = start;
      setMentionSearchQuery('');
      setMentionPickerOpen(true);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 1, start + 1);
      }, 0);
      return;
    }

    // Handle colon (:) - let it be inserted, then open picker
    // Check for colon key press - can be ':' or Shift+';' depending on keyboard layout
    const isColonKey = e.key === ':' || (e.shiftKey && e.key === ';') || e.keyCode === 186;
    
    if (isColonKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const textarea = e.currentTarget;
      const cursorPos = textarea.selectionStart || 0;
      const textBeforeCursor = message.substring(0, cursorPos);
      
      // Check if it's part of a URL pattern (http://, https://)
      const isUrlPattern = /https?:\/?$/i.test(textBeforeCursor.trim());
      
      // Allow colon to be inserted and open picker unless it's part of a URL
      // Don't preventDefault - let colon be inserted normally
      // Colon position will be tracked in handleInputChange after insertion
      if (isUrlPattern) {
        // For URLs, don't open picker - colon will be inserted normally
        colonPositionRef.current = null;
      }
    }

    // Note: Closing picker when user continues typing is handled in handleInputChange
    // to detect actual text changes and cursor movement.
  };

  // Handle keyboard input for colon and @ detection
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const textarea = e.target;
    const cursorPos = textarea.selectionStart || 0;
    
    // Check if @ was just typed (detect @ insertion) - group and direct chats
    if (
      value.length > message.length &&
      cursorPos > 0 &&
      value[cursorPos - 1] === '@' &&
      conversation &&
      participants.length > 0
    ) {
      // Store @ position
      atPositionRef.current = cursorPos - 1;
      setMentionSearchQuery('');
      setMentionPickerOpen(true);
      setMessage(value);
      // Don't return - continue to handle typing indicator
    }
    // Check if user is typing after @ (update search query)
    else if (mentionPickerOpen && atPositionRef.current !== null && cursorPos > atPositionRef.current) {
      // Extract text between @ and cursor
      const textAfterAt = value.substring(atPositionRef.current + 1, cursorPos);
      // Check if there's a space (mention ended) or newline
      if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
        // Mention ended, close picker
        setMentionPickerOpen(false);
        setMentionSearchQuery('');
        atPositionRef.current = null;
        setMessage(value);
      } else {
        // Update search query
        setMentionSearchQuery(textAfterAt);
        setMessage(value);
      }
    }
    // Check if colon was just typed (detect colon insertion)
    else if (value.length > message.length && cursorPos > 0 && value[cursorPos - 1] === ':') {
      const textBeforeColon = value.substring(0, cursorPos - 1);
      const isUrlPattern = /https?:\/?$/i.test(textBeforeColon.trim());
      
      // If colon was typed and it's not part of URL, track it and open emoji picker
      if (!isUrlPattern) {
        // Store colon position (colon is already in the value)
        colonPositionRef.current = cursorPos - 1;
        
        // Update message (colon is already inserted)
        setMessage(value);
        
        // Open emoji picker
        setEmojiPickerOpen(true);
        
        // Don't return - continue to update message and handle typing indicator
      } else {
        // URL pattern - just update message normally
        setMessage(value);
      }
    } else {
      // No colon detected - normal input change
      setMessage(value);
      
      // If picker is open and colon is pending, but user is typing something else
      // (this handles the case where user continues typing after colon)
      if (emojiPickerOpen && colonPositionRef.current !== null) {
        // Check if cursor moved past colon position (user is typing after colon)
        // Colon is at colonPositionRef.current, so if cursor is after it, user is typing
        if (cursorPos > colonPositionRef.current) {
          // User continued typing - close picker, keep colon
          colonPositionRef.current = null;
          setEmojiPickerOpen(false);
        }
      }
      
      // If mention picker is open but cursor moved away from @, close it
      if (mentionPickerOpen && atPositionRef.current !== null) {
        const textAfterAt = value.substring(atPositionRef.current + 1, cursorPos);
        if (textAfterAt.includes(' ') || textAfterAt.includes('\n') || cursorPos <= atPositionRef.current) {
          setMentionPickerOpen(false);
          setMentionSearchQuery('');
          atPositionRef.current = null;
        }
      }
    }
    
    // Update cursor position ref when input changes (for emoji insertion via button)
    // Only update if not already set, no colon is pending, and picker is not open
    // (when picker is open, we want to preserve the position from when it was opened)
    if (cursorPositionRef.current === null && colonPositionRef.current === null && !emojiPickerOpen) {
      cursorPositionRef.current = cursorPos;
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Handle typing indicator with debouncing and throttle
    if (value.length > 0) {
      const now = Date.now();
      const canUpdate =
        !isTyping &&
        now - lastTypingUpdateRef.current >= TYPING_THROTTLE_MS;
      if (canUpdate) {
        setIsTyping(true);
        onTyping(true);
        lastTypingUpdateRef.current = now;
      }
      
      // Set timeout to clear typing indicator after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTyping(false);
        lastTypingUpdateRef.current = 0;
        typingTimeoutRef.current = null;
      }, TYPING_THROTTLE_MS);
    } else {
      // Clear typing immediately if input is empty
      if (isTyping) {
        setIsTyping(false);
        onTyping(false);
        lastTypingUpdateRef.current = 0;
      }
    }
  };

  // Get participant details for reply preview
  const participantDetails = useMemo(() => {
    if (!conversation) return {};
    return conversation.participantDetails || {};
  }, [conversation]);

  return (
    <InputContainer $isTablet={isTablet} $isMobile={isMobile}>
      {/* Reply Preview - Full Width Row */}
      {replyingTo && (
        <ReplyPreviewWrapper>
          <ReplyPreview
            repliedToMessage={replyingTo}
            participantDetails={participantDetails}
            onClick={() => {
              // Scroll to replied message when clicking preview
              const event = new globalThis.CustomEvent('jumpToMessage', {
                detail: { messageId: replyingTo.id },
              });
              window.dispatchEvent(event);
            }}
          />
          {onCancelReply && (
            <CancelReplyButton
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={onCancelReply}
              title={t('threading.cancelReply') || 'Cancel reply'}
            />
          )}
        </ReplyPreviewWrapper>
      )}
      
      {/* Input Row - Full Width Row Below Reply Preview */}
      <InputRow>
        <StyledTextArea
          ref={textAreaRef}
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder=""
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={disabled}
        />
        
        <EmojiPicker 
          onEmojiSelect={handleEmojiSelect} 
          placement="top"
          open={emojiPickerOpen}
          onOpenChange={(open) => {
            setEmojiPickerOpen(open);
            // If picker closes without emoji selection, clear colon tracking
            // (colon stays in message, which is what we want)
            if (!open && colonPositionRef.current !== null) {
              colonPositionRef.current = null;
            }
          }}
        >
          <EmojiButton
            icon={<SmileOutlined />}
            disabled={disabled}
            type="text"
            $isMobile={isMobile}
            onMouseDown={() => {
              // Capture cursor position on mousedown (before focus changes)
              // This ensures we get the position before the button click causes focus loss
              if (textAreaRef.current) {
                const textarea = textAreaRef.current;
                // Capture position immediately - use selectionStart, fallback to selectionEnd, then message length
                const currentPos = textarea.selectionStart ?? textarea.selectionEnd ?? message.length;
                
                cursorPositionRef.current = currentPos;
              } else {
                // Fallback: if no textarea ref, use end of message
                cursorPositionRef.current = message.length;
              }
            }}
            onClick={(e) => {
              // Prevent event bubbling
              e.preventDefault();
              e.stopPropagation();
              
              // Open picker (cursor position was already captured in onMouseDown)
              setEmojiPickerOpen(true);
            }}
          />
        </EmojiPicker>
        
        <SendButton
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          $isMobile={isMobile}
        />
      </InputRow>
      
      {/* Mention Picker */}
      {conversation && participants.length > 0 && (
        <MentionPickerWrapper>
          <MentionPicker
            participants={participants}
            searchQuery={mentionSearchQuery}
            onSelect={handleMentionSelect}
            open={mentionPickerOpen}
            onClose={() => {
              setMentionPickerOpen(false);
              setMentionSearchQuery('');
              atPositionRef.current = null;
            }}
            placement="bottom"
          />
        </MentionPickerWrapper>
      )}
    </InputContainer>
  );
};

